/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikProps } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { CheckInCircleSolid, Lock, Send, Warning } from "@gaming/xds-react-js-icons";

import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import {
  FormSectionHeader,
  FormSectionStatus,
  getFormSectionStatus,
} from "../../components/FormSectionHeader";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { SubFormNavigationProps } from "../../components/SubForm";
import {
  partnerAccessDeniedErrors,
  partnerAccessDeniedPath,
  partnerQueryParameters,
  xboxOnboardSiteLocationPrefix,
} from "../../constants";
import {
  IdXboxIntroFormId,
  IdXboxIntroSubFormsId,
  IdXboxIntroSubFormsIdsOrder,
} from "../../constants/formConstants";
import {
  PartnerAccessLevel,
  PartnerAccountRequirement,
  usePartnerRouteGuard,
} from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import { IdXboxIntroContactDataType, IdXboxIntroStudioDataType } from "../../models/forms";
import { Programs } from "../../models/programsConfig";
import { SubFormsRenderingModel } from "../../models/sharedFormsModels";
import { crmActions } from "../../redux/actions/crmApiActions";
import { idXboxProgramActions } from "../../redux/actions/idXboxProgramActions";
import { programsActions } from "../../redux/actions/programsActions";
import { statusActions } from "../../redux/actions/statusActions";
import { OnboardingRootState } from "../../redux/reducers/rootReducer";
import {
  getCurrentError,
  getIdXboxProgramData,
  getIdXboxProgramError,
  getIdXboxProgramIsLoading,
  getIsSavingForm,
  getIsSubmitting,
  getSubFormData,
} from "../../redux/selectors";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import { ContactInformationSubForm } from "./ContactInformationSubForm";
import * as styles from "./IdXboxProgramPage.module.css";
import { StudioInformationSubForm } from "./StudioInformationSubForm";

const IdXboxProgramPage: React.FC = () => {
  const isPageReady = usePartnerRouteGuard(
    PartnerAccessLevel.EnrollmentFinished,
    PartnerAccountRequirement.NonAaa
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const formRefsMap = useRef<Record<string, React.RefObject<FormikProps<any>>>>({});
  const isSaving = useSelector(getIsSavingForm);
  const isSubmitting = useSelector(getIsSubmitting);
  const [isSavePending, setIsSavePending] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [sectionStatus, setSectionStatus] = useState<Record<string, FormSectionStatus>>({});
  const [startedSections, setStartedSections] = useState<Set<string>>(new Set());

  const isIdXboxProgramLoading = useSelector(getIdXboxProgramIsLoading);
  const idXboxProgramError = useSelector(getIdXboxProgramError);
  const currentError = useSelector(getCurrentError);
  const idXboxProgramData = useSelector(getIdXboxProgramData);
  const canSubmitIdApplication =
    useSelector(getXboxOnboardData)?.hubPermissions.canSubmitIdApplication === true;
  const contactFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, IdXboxIntroFormId, IdXboxIntroSubFormsId.ContactInformation)
  ) as IdXboxIntroContactDataType | undefined;
  const studioFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, IdXboxIntroFormId, IdXboxIntroSubFormsId.StudioInformation)
  ) as IdXboxIntroStudioDataType | undefined;
  const isCrmDataLoaded = !isIdXboxProgramLoading && idXboxProgramData !== null;
  const isEnrolled =
    idXboxProgramData?.status === "approved" || idXboxProgramData?.status === "submitted";
  const isUkraineOnboardingHold = idXboxProgramData?.isUkraineOnboardingHold === true;
  const isApplicationReadOnly = isEnrolled || isUkraineOnboardingHold;
  const enrollmentStatus = idXboxProgramData?.enrollmentStatus;
  const isFastTrackAccount = idXboxProgramData?.isFastTrackAccount === true;

  const [openSections, setOpenSections] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!idXboxProgramData) {
      return;
    }

    const persistedStartedSections = new Set<string>();
    if (idXboxProgramData.contact.isStarted) {
      persistedStartedSections.add(IdXboxIntroSubFormsId.ContactInformation);
    }
    if (idXboxProgramData.studio.isStarted) {
      persistedStartedSections.add(IdXboxIntroSubFormsId.StudioInformation);
    }
    setStartedSections(persistedStartedSections);
  }, [idXboxProgramData]);

  useEffect(() => {
    if (!isPageReady) {
      return;
    }

    if (!canSubmitIdApplication) {
      history.replace(
        `${partnerAccessDeniedPath}?${partnerQueryParameters.accessDeniedError}=${partnerAccessDeniedErrors.admin}`
      );
      return;
    }

    dispatch(statusActions.updateCurrentProgram(Programs.IdXbox));
    dispatch(idXboxProgramActions.fetchData.start());
  }, [canSubmitIdApplication, dispatch, history, isPageReady]);

  const [
    applicationTitle,
    sidebarTitle,
    sidebarDescription,
    developerSupportTitle,
    developerSupportDesc,
    publishingSupportTitle,
    publishingSupportDesc,
    fullXboxServicesTitle,
    fullXboxServicesDesc,
    marketingSupportTitle,
    marketingSupportDesc,
    dealConsiderationTitle,
    dealConsiderationDesc,
    conceptDetailsTitle,
    conceptDetailsSubtitle,
    partnerInfoTitle,
    partnerInfoSubtitle,
    idXboxBreadcrumbLabel,
    cancelButtonLabel,
    savingLabel,
    saveAndCloseButtonLabel,
    submittingLabel,
    submitButtonLabel,
    applicationCompleteTitle,
    applicationCompleteMessage,
    applicationProgressTitle,
    gameConceptApprovedLabel,
    gameConceptPendingLabel,
    tlaContractSignedLabel,
    tlaContractSentMessage,
    tlaContractPendingLabel,
    gdkContractSignedLabel,
    gdkContractSentMessage,
    gdkContractPendingLabel,
    firstNameLabel,
    lastNameLabel,
    businessTitleLabel,
    emailLabel,
    phoneLabel,
    addressLabel,
    studioNameLabel,
    businessNameLabel,
    studioWebsiteLabel,
    actsAsLabel,
    trailersLinksLabel,
    middlewareEnginesLabel,
    loadingStatus,
    errorStatus,
    ukraineOnboardingHoldTitle,
    ukraineOnboardingHoldMessage,
    emailCommunicationsNotice,
  ] = useStrings(
    "idXboxApplicationTitle",
    "idXboxProgramSidebarTitle",
    "idXboxProgramSidebarDescription",
    "idXboxDeveloperSupport",
    "idXboxDeveloperSupportDesc",
    "idXboxPublishingTools",
    "idXboxPublishingToolsDesc",
    "idXboxXboxServices",
    "idXboxXboxServicesDesc",
    "idXboxMarketingOpportunities",
    "idXboxMarketingOpportunitiesDesc",
    "idXboxGamePassConsideration",
    "idXboxGamePassConsiderationDesc",
    "conceptDetailsAccordionTitle",
    "conceptDetailsAccordionSubtitle",
    "partnerInformationAccordionTitle",
    "partnerInformationAccordionSubtitle",
    "xboxOnboardIdXboxTitle",
    "cancelButton",
    "ticketSavingLabel",
    "saveAndCloseButton",
    "ticketSubmittingLabel",
    "xboxOnboardSubmitButton",
    "idXboxApplicationCompleteTitle",
    "idXboxApplicationCompleteMessage",
    "idXboxApplicationProgressTitle",
    "idXboxGameConceptApprovedLabel",
    "idXboxGameConceptPendingLabel",
    "idXboxTlaContractSignedLabel",
    "idXboxTlaContractSentMessage",
    "idXboxTlaContractPendingLabel",
    "idXboxGdkContractSignedLabel",
    "infoBoxAdditionalAgreementsSignGdkActionRequested",
    "idXboxGdkContractPendingLabel",
    "xboxOnboardFirstName",
    "xboxOnboardLastName",
    "xboxOnboardBusinessTitle",
    "xboxOnboardEmail",
    "phone",
    "address",
    "xboxOnboardStudioName",
    "idXboxBusinessNameLabel",
    "studioWebsite",
    "idXboxActsAsLabel",
    "idXboxTrailersLinksLabel",
    "idXboxMiddlewareEnginesLabel",
    "idXboxApplicationLoadingStatus",
    "somethingWentWrong",
    "idXboxUkraineOnboardingHoldTitle",
    "idXboxUkraineOnboardingHoldMessage",
    "idXboxEmailCommunicationsNotice"
  );

  const getFormRef = useCallback((subFormId: string): React.RefObject<FormikProps<any>> => {
    if (!formRefsMap.current[subFormId]) {
      formRefsMap.current[subFormId] = React.createRef<FormikProps<any>>();
    }
    return formRefsMap.current[subFormId];
  }, []);

  const onSaveFormToCrm = useCallback(
    (subFormId: string) => {
      dispatch(
        crmActions.saveCrmData.start(Programs.IdXbox, IdXboxIntroFormId, subFormId, undefined)
      );
    },
    [dispatch]
  );

  const onForwardNavigation = useCallback(() => {
    // No-op in accordion mode
  }, []);

  const getSubFormProps = useCallback(
    (subFormId: string): SubFormNavigationProps => ({
      formRef: getFormRef(subFormId),
      onCancel: undefined,
      onBackNavigation: undefined,
      onForwardNavigation,
      onSaveFormToCrm,
      skipScrollToTop: true,
      hideNavigation: true,
    }),
    [onForwardNavigation, onSaveFormToCrm, getFormRef]
  );

  const subFormsList: ReadonlyDictionary<SubFormsRenderingModel> = {
    [IdXboxIntroSubFormsId.ContactInformation]: {
      title: conceptDetailsTitle,
      renderSubForm: (props: SubFormNavigationProps) => (
        <ContactInformationSubForm
          {...props}
          skipNdaDisable
          lockCountry
          disableAll={isApplicationReadOnly}
        />
      ),
    },
    [IdXboxIntroSubFormsId.StudioInformation]: {
      title: partnerInfoTitle,
      renderSubForm: (props: SubFormNavigationProps) => (
        <StudioInformationSubForm
          {...props}
          skipNdaDisable
          lockStudioName
          lockBusinessName
          disableAll={isApplicationReadOnly}
        />
      ),
    },
  };

  const subFormSubtitles: Record<string, string> = {
    [IdXboxIntroSubFormsId.ContactInformation]: conceptDetailsSubtitle,
    [IdXboxIntroSubFormsId.StudioInformation]: partnerInfoSubtitle,
  };

  const persistAllFormsToRedux = useCallback(() => {
    for (const subFormId of IdXboxIntroSubFormsIdsOrder) {
      const ref = formRefsMap.current[subFormId];
      if (ref?.current?.values) {
        dispatch(
          programsActions.updateSubFormData(Programs.IdXbox, {
            formId: IdXboxIntroFormId,
            subFormId,
            subForm: {
              ...ref.current.values,
              isStarted: startedSections.has(subFormId) || ref.current.values.isStarted === true,
            },
          })
        );
      }
    }
  }, [dispatch, startedSections]);

  const handleSave = useCallback(() => {
    persistAllFormsToRedux();
    for (const subFormId of IdXboxIntroSubFormsIdsOrder) {
      onSaveFormToCrm(subFormId);
    }
    setIsSavePending(true);
  }, [persistAllFormsToRedux, onSaveFormToCrm]);

  const handleEnroll = useCallback(() => {
    persistAllFormsToRedux();
    dispatch(crmActions.enrollIdXbox.start(undefined, history));
  }, [dispatch, history, persistAllFormsToRedux]);

  useEffect(() => {
    if (isSavePending && !isSaving) {
      setIsSavePending(false);
      history.push(`${xboxOnboardSiteLocationPrefix}/dashboard`);
    }
  }, [isSavePending, isSaving, history]);

  const handleSectionClick = (index: number) => {
    const subFormId = IdXboxIntroSubFormsIdsOrder[index];
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        const ref = formRefsMap.current[subFormId];
        if (ref?.current?.values) {
          dispatch(
            programsActions.updateSubFormData(Programs.IdXbox, {
              formId: IdXboxIntroFormId,
              subFormId,
              subForm: {
                ...ref.current.values,
                isStarted: true,
              },
            })
          );
        }
        next.delete(index);
      } else {
        if (!startedSections.has(subFormId)) {
          const existingData =
            subFormId === IdXboxIntroSubFormsId.ContactInformation
              ? idXboxProgramData?.contact
              : idXboxProgramData?.studio;
          setStartedSections(started => new Set(started).add(subFormId));
          if (existingData) {
            dispatch(
              programsActions.updateSubFormData(Programs.IdXbox, {
                formId: IdXboxIntroFormId,
                subFormId,
                subForm: {
                  ...existingData,
                  isStarted: true,
                },
              })
            );
          }
        }
        next.add(index);
      }
      return next;
    });
  };

  // Validate forms for enroll button enablement
  // Only check that required editable fields are filled — base fields are locked from CRM
  const validateForms = useCallback(async () => {
    // Prefer live Formik values, then the latest Redux values retained after an accordion closes.
    const contactRef = formRefsMap.current[IdXboxIntroSubFormsId.ContactInformation];
    const studioRef = formRefsMap.current[IdXboxIntroSubFormsId.StudioInformation];
    const currentContactData =
      contactRef?.current?.values ?? contactFormData ?? idXboxProgramData?.contact;
    const currentStudioData =
      studioRef?.current?.values ?? studioFormData ?? idXboxProgramData?.studio;

    if (!currentContactData || !currentStudioData) {
      setIsFormValid(false);
      setSectionStatus({
        [IdXboxIntroSubFormsId.ContactInformation]: "notStarted",
        [IdXboxIntroSubFormsId.StudioInformation]: "notStarted",
      });
      return;
    }

    // Base contact fields (locked) — just verify they exist
    const baseContactValid = !!(
      currentContactData.firstName &&
      currentContactData.lastName &&
      currentContactData.businessTitle &&
      currentContactData.email &&
      currentContactData.countryOrRegion &&
      currentContactData.addressLine1 &&
      currentContactData.city &&
      currentContactData.stateRegion &&
      currentContactData.postalCode &&
      currentContactData.phone
    );

    // ID-specific required studio fields
    const studioValid = !!(
      currentStudioData.studioName &&
      currentStudioData.businessName?.trim() &&
      currentStudioData.thisCompanyActsAs?.length > 0 &&
      currentStudioData.middlewareEnginesUsed?.trim()
    );

    const contactStarted = startedSections.has(IdXboxIntroSubFormsId.ContactInformation);
    const studioStarted = startedSections.has(IdXboxIntroSubFormsId.StudioInformation);

    setSectionStatus({
      [IdXboxIntroSubFormsId.ContactInformation]: getFormSectionStatus(
        contactStarted,
        baseContactValid
      ),
      [IdXboxIntroSubFormsId.StudioInformation]: getFormSectionStatus(studioStarted, studioValid),
    });

    setIsFormValid(baseContactValid && studioValid);
  }, [contactFormData, idXboxProgramData, startedSections, studioFormData]);

  // Re-validate when redux data changes or sections toggle
  useEffect(() => {
    validateForms();
  }, [validateForms, openSections]);

  // Re-validate periodically while forms are open to catch live typing
  useEffect(() => {
    if (openSections.size === 0) {
      return;
    }
    const interval = setInterval(validateForms, 500);
    return () => clearInterval(interval);
  }, [validateForms, openSections]);

  const sidebarBenefits = [
    { title: fullXboxServicesTitle, description: fullXboxServicesDesc },
    { title: marketingSupportTitle, description: marketingSupportDesc },
    { title: developerSupportTitle, description: developerSupportDesc },
    { title: dealConsiderationTitle, description: dealConsiderationDesc },
    { title: publishingSupportTitle, description: publishingSupportDesc },
  ];

  if (!isPageReady || !canSubmitIdApplication || !isCrmDataLoaded) {
    return (
      <PartnerPageLayout breadcrumbLabel={idXboxBreadcrumbLabel}>
        {idXboxProgramError ? (
          <p className={styles.errorMessage} role="alert">
            {errorStatus}
          </p>
        ) : (
          <div role="status" aria-live="polite" aria-label={loadingStatus} aria-busy="true">
            <PageLoadingSpinner />
          </div>
        )}
      </PartnerPageLayout>
    );
  }

  const operationStatus = isSubmitting
    ? submittingLabel
    : isSaving || isSavePending
    ? savingLabel
    : "";

  return (
    <>
      <PartnerPageLayout breadcrumbLabel={idXboxBreadcrumbLabel}>
        <div
          className={styles.screenReaderOnly}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {operationStatus}
        </div>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>{applicationTitle}</h1>
          </div>
          <div className={styles.headerRight}>
            {!isApplicationReadOnly &&
              (() => {
                const isSaveInProgress = isSavePending;
                const isBusy = isSaving || isSaveInProgress || isSubmitting;

                return (
                  <>
                    <div className={styles.actionButtons}>
                      <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={() => history.push(`${xboxOnboardSiteLocationPrefix}/dashboard`)}
                        disabled={isBusy}
                      >
                        {cancelButtonLabel}
                      </button>
                      <button
                        type="button"
                        className={styles.saveButton}
                        onClick={handleSave}
                        disabled={isBusy}
                      >
                        {isSaveInProgress ? savingLabel : saveAndCloseButtonLabel}
                      </button>
                      <button
                        type="button"
                        className={styles.enrollButton}
                        onClick={handleEnroll}
                        disabled={isBusy || !isFormValid}
                      >
                        {isSubmitting ? submittingLabel : submitButtonLabel}
                      </button>
                    </div>
                  </>
                );
              })()}
          </div>
        </div>

        {currentError && (
          <p className={styles.errorMessage} role="alert">
            {errorStatus}
          </p>
        )}

        <div className={styles.body} aria-busy={Boolean(operationStatus)}>
          <div className={styles.mainColumn}>
            {!isApplicationReadOnly && (
              <p className={styles.emailCommunicationsNotice}>{emailCommunicationsNotice}</p>
            )}
            {isApplicationReadOnly ? (
              <>
                {isUkraineOnboardingHold && (
                  <div className={styles.ukraineHoldBanner} role="status">
                    <Icon
                      aria-hidden="true"
                      className={styles.ukraineHoldIcon}
                      icon={Warning}
                      iconSize={IconSize.Custom}
                    />
                    <div className={styles.ukraineHoldContent}>
                      <h2 className={styles.ukraineHoldTitle}>{ukraineOnboardingHoldTitle}</h2>
                      <p className={styles.ukraineHoldMessage}>{ukraineOnboardingHoldMessage}</p>
                    </div>
                  </div>
                )}
                {isEnrolled && enrollmentStatus && (
                  <div className={styles.enrollmentStatusSection} role="status" aria-live="polite">
                    {enrollmentStatus.applicationComplete ? (
                      <div className={styles.enrollmentStatusComplete}>
                        <span className={styles.enrollmentStatusIcon} aria-hidden="true">
                          <CheckInCircleSolid width={24} height={24} />
                        </span>
                        <div>
                          <h4 className={styles.enrollmentStatusTitle}>
                            {applicationCompleteTitle}
                          </h4>
                          <p className={styles.enrollmentStatusMessage}>
                            {applicationCompleteMessage}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.enrollmentStatusPending}>
                        <h4 className={styles.enrollmentStatusTitle}>{applicationProgressTitle}</h4>
                        <div className={styles.enrollmentChecklist}>
                          <div
                            className={
                              enrollmentStatus.gameConceptApproved
                                ? styles.checklistItemDone
                                : styles.checklistItemLocked
                            }
                          >
                            <span className={styles.checklistIcon} aria-hidden="true">
                              {enrollmentStatus.gameConceptApproved ? (
                                <CheckInCircleSolid width={16} height={16} />
                              ) : (
                                <Lock width={16} height={16} />
                              )}
                            </span>
                            <span>
                              {enrollmentStatus.gameConceptApproved
                                ? gameConceptApprovedLabel
                                : gameConceptPendingLabel}
                            </span>
                          </div>
                          {!isFastTrackAccount && (
                            <div
                              className={
                                enrollmentStatus.tlaSigned
                                  ? styles.checklistItemDone
                                  : enrollmentStatus.tlaSent
                                  ? styles.checklistItemPending
                                  : styles.checklistItemLocked
                              }
                            >
                              <span className={styles.checklistIcon} aria-hidden="true">
                                {enrollmentStatus.tlaSigned ? (
                                  <CheckInCircleSolid width={16} height={16} />
                                ) : enrollmentStatus.tlaSent ? (
                                  <Send width={16} height={16} />
                                ) : (
                                  <Lock width={16} height={16} />
                                )}
                              </span>
                              <span>
                                {enrollmentStatus.tlaSigned
                                  ? tlaContractSignedLabel
                                  : enrollmentStatus.tlaSent
                                  ? tlaContractSentMessage
                                  : tlaContractPendingLabel}
                              </span>
                            </div>
                          )}
                          <div
                            className={
                              enrollmentStatus.gdkSigned
                                ? styles.checklistItemDone
                                : enrollmentStatus.gdkSent
                                ? styles.checklistItemPending
                                : styles.checklistItemLocked
                            }
                          >
                            <span className={styles.checklistIcon} aria-hidden="true">
                              {enrollmentStatus.gdkSigned ? (
                                <CheckInCircleSolid width={16} height={16} />
                              ) : enrollmentStatus.gdkSent ? (
                                <Send width={16} height={16} />
                              ) : (
                                <Lock width={16} height={16} />
                              )}
                            </span>
                            <span>
                              {enrollmentStatus.gdkSigned
                                ? gdkContractSignedLabel
                                : enrollmentStatus.gdkSent
                                ? gdkContractSentMessage
                                : gdkContractPendingLabel}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className={styles.accordionContainer}>
                  {[
                    {
                      title: conceptDetailsTitle,
                      subtitle: conceptDetailsSubtitle,
                      fields: [
                        { label: firstNameLabel, value: idXboxProgramData?.contact.firstName },
                        { label: lastNameLabel, value: idXboxProgramData?.contact.lastName },
                        {
                          label: businessTitleLabel,
                          value: idXboxProgramData?.contact.businessTitle,
                        },
                        { label: emailLabel, value: idXboxProgramData?.contact.email },
                        { label: phoneLabel, value: idXboxProgramData?.contact.phone },
                        {
                          label: addressLabel,
                          value: [
                            idXboxProgramData?.contact.addressLine1,
                            idXboxProgramData?.contact.addressLine2,
                            [
                              idXboxProgramData?.contact.city,
                              idXboxProgramData?.contact.stateRegion,
                              idXboxProgramData?.contact.postalCode,
                            ]
                              .filter(Boolean)
                              .join(", "),
                          ]
                            .filter(Boolean)
                            .join(", "),
                        },
                      ],
                    },
                    {
                      title: partnerInfoTitle,
                      subtitle: partnerInfoSubtitle,
                      fields: [
                        { label: studioNameLabel, value: idXboxProgramData?.studio.studioName },
                        { label: businessNameLabel, value: idXboxProgramData?.studio.businessName },
                        {
                          label: studioWebsiteLabel,
                          value: idXboxProgramData?.studio.studioWebsite,
                        },
                        {
                          label: actsAsLabel,
                          value: idXboxProgramData?.studio.thisCompanyActsAs?.join(", "),
                        },
                        {
                          label: trailersLinksLabel,
                          value: idXboxProgramData?.studio.publicTrailersLinks?.join(", "),
                        },
                        {
                          label: middlewareEnginesLabel,
                          value: idXboxProgramData?.studio.middlewareEnginesUsed,
                        },
                      ],
                    },
                  ].map((section, index) => {
                    const isOpen = openSections.has(index);
                    return (
                      <div key={section.title} className={styles.accordionSection}>
                        <button
                          id={`id-xbox-readonly-accordion-${index}-header`}
                          className={`${styles.accordionHeader} ${
                            isOpen ? styles.accordionHeaderOpen : ""
                          }`}
                          onClick={() => handleSectionClick(index)}
                          type="button"
                          aria-expanded={isOpen}
                          aria-controls={`id-xbox-readonly-accordion-${index}-panel`}
                        >
                          <span className={styles.accordionTitle}>
                            <CheckInCircleSolid
                              className={styles.checkIconValid}
                              aria-hidden="true"
                            />
                            <span className={styles.accordionTitleBlock}>
                              <span className={styles.accordionTitleText}>{section.title}</span>
                              <span className={styles.accordionSubtitle}>{section.subtitle}</span>
                            </span>
                          </span>
                          <span
                            aria-hidden="true"
                            className={`${styles.accordionChevron} ${
                              isOpen ? styles.accordionChevronOpen : ""
                            }`}
                          >
                            &#8250;
                          </span>
                        </button>
                        {isOpen && (
                          <dl
                            id={`id-xbox-readonly-accordion-${index}-panel`}
                            className={styles.readOnlyContent}
                            role="region"
                            aria-labelledby={`id-xbox-readonly-accordion-${index}-header`}
                          >
                            {section.fields
                              .filter(f => f.value)
                              .map(f => (
                                <div key={f.label} className={styles.readOnlyField}>
                                  <dt className={styles.readOnlyLabel}>{f.label}</dt>
                                  <dd className={styles.readOnlyValue}>{f.value}</dd>
                                </div>
                              ))}
                          </dl>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <div className={styles.accordionContainer}>
                  {IdXboxIntroSubFormsIdsOrder.map((subFormId, index) => {
                    const subFormModel = subFormsList[subFormId];
                    const isOpen = openSections.has(index);
                    return (
                      <div key={subFormId} className={styles.accordionSection}>
                        <button
                          id={`id-xbox-accordion-${index}-header`}
                          className={`${styles.accordionHeader} ${
                            isOpen ? styles.accordionHeaderOpen : ""
                          }`}
                          onClick={() => handleSectionClick(index)}
                          type="button"
                          aria-expanded={isOpen}
                          aria-controls={`id-xbox-accordion-${index}-panel`}
                        >
                          <FormSectionHeader
                            status={sectionStatus[subFormId] ?? "notStarted"}
                            title={subFormModel.title}
                            subtitle={subFormSubtitles[subFormId]}
                          />
                          <span
                            aria-hidden="true"
                            className={`${styles.accordionChevron} ${
                              isOpen ? styles.accordionChevronOpen : ""
                            }`}
                          >
                            &#8250;
                          </span>
                        </button>
                        {isOpen && (
                          <div
                            id={`id-xbox-accordion-${index}-panel`}
                            className={styles.accordionContent}
                            role="region"
                            aria-labelledby={`id-xbox-accordion-${index}-header`}
                          >
                            {subFormModel.renderSubForm(getSubFormProps(subFormId))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <aside className={styles.sidePanel}>
            <div className={styles.sidebarCard}>
              <h3 className={styles.sidebarTitle}>{sidebarTitle}</h3>
              <p className={styles.sidebarDescription}>{sidebarDescription}</p>
              <div className={styles.sidebarBenefitsList}>
                {sidebarBenefits.map((benefit, idx) => (
                  <React.Fragment key={benefit.title}>
                    {idx > 0 && <hr className={styles.sidebarBenefitSeparator} />}
                    <div className={styles.sidebarBenefit}>
                      <h4 className={styles.sidebarBenefitTitle}>{benefit.title}</h4>
                      <p className={styles.sidebarBenefitDescription}>{benefit.description}</p>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </PartnerPageLayout>
    </>
  );
};

export default IdXboxProgramPage;
